import React, { useMemo } from "react"
import { Avatar, Box, Typography, useTheme } from "@mui/material"
import { Link } from "react-router-dom"
import _ from "lodash"

import { getIcon } from "utils/icon"
import { getFeatureDetail, IOccupantDetail } from "utils/feature"
import { useNewShops } from "hooks/new-shop/useNewShops"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"

import { PageLayout } from "layouts/PageLayout"

import { EmptyData } from "components/EmptyData"
import { Head } from "components/Head"
import { StorefrontIcon } from "components/icons"
import { ImageCard } from "components/ImageCard"
import { VenueSelector } from "components/VenueSelector"

export const NewShops = ({ Layout = PageLayout }) => {
  const { t } = useTranslation()
  const { isMultiOrdinalVenue, occupants } = useVenue()
  const {
    newOccupants,
    comingSoonOccupants,
    isMultiVenueProject,
    venues,
    venueFilter,
    locationVenueId,
    hasData,
    filterByVenueId,
  } = useNewShops(occupants)
  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.newShops) || <StorefrontIcon />,
    [theme]
  )
  const { styleOverrides } = theme.components?.[`NewShopsPage`] || {}
  const {
    emptyDataContainer,
    gridContainer,
    venueSelector,
    // New shop
    newShopImageCard,
    newShopFallbackImageIconWrapper,
    newShopFallbackImageIcon,
    newShopAvatar,
    newShopAvatarFallBack,
    // Coming soon shop
    comingSoonShopImageCard,
    comingSoonShopFallbackImageIconWrapper,
    comingSoonShopFallbackImageIcon,
    comingSoonShopAvatar,
    comingSoonShopAvatarFallBack,
    pageLayout,
  } = styleOverrides
  return (
    <>
      <Head title={t("New Shops")} />
      <Layout sx={pageLayout}>
        <Box>
          {hasData ? (
            <Box sx={gridContainer}>
              {newOccupants.map((newOccupant, index) => {
                const {
                  group,
                  level,
                  logo,
                  featuredImage,
                  name,
                  boothName,
                  categoryIcon,
                  subCategory,
                } = (getFeatureDetail(newOccupant) as IOccupantDetail) || {}

                const levelText = isMultiOrdinalVenue
                  ? t("Level Text", { level: t(level) })
                  : ""
                const groupText = t(group)
                return (
                  <ImageCard
                    variant="full-image"
                    key={`new-shop-${newOccupant.id}`}
                    sx={newShopImageCard}
                    component={Link}
                    to={`/maps/place/${newOccupant?.id}`}
                    title={t(name)}
                    subheader={
                      <>
                        <Typography
                          component="span"
                          //@ts-ignore
                          variant="subtitle"
                          className="sub-category-text"
                        >
                          {t(subCategory)}
                        </Typography>
                        {boothName && (
                          <Typography
                            component="span"
                            //@ts-ignore
                            variant="subtitle"
                            className="booth-name-text"
                          >
                            {`${t("Booth")} ${t(boothName)}`}
                          </Typography>
                        )}
                        <Typography
                          className="location-text"
                          component="span"
                          //@ts-ignore
                          variant="subtitle"
                        >
                          {_.compact([levelText, groupText]).join(" ")}
                        </Typography>
                      </>
                    }
                    image={featuredImage}
                    fallbackImageIcon={
                      <Box sx={newShopFallbackImageIconWrapper}>
                        {getIcon(categoryIcon, newShopFallbackImageIcon, {
                          imageIconProps: {
                            solidColorFilterId: "newShop_cover_filter",
                          },
                        })}
                      </Box>
                    }
                    avatar={
                      <Avatar src={logo} sx={newShopAvatar}>
                        {getIcon(categoryIcon, newShopAvatarFallBack, {
                          imageIconProps: {
                            solidColorFilterId: "newShop_avatar_filter",
                          },
                        })}
                      </Avatar>
                    }
                    tag={t("New Shops")}
                  />
                )
              })}
              {comingSoonOccupants.map((comingSoonOccupant, index) => {
                const {
                  group,
                  level,
                  logo,
                  featuredImage,
                  name,
                  boothName,
                  categoryIcon,
                  subCategory,
                } =
                  (getFeatureDetail(comingSoonOccupant) as IOccupantDetail) ||
                  {}

                const levelText = isMultiOrdinalVenue
                  ? t("Level Text", { level: t(level) })
                  : ""
                const groupText = t(group)

                return (
                  <ImageCard
                    variant="full-image"
                    key={`coming-soon-${comingSoonOccupant.id}`}
                    sx={comingSoonShopImageCard}
                    title={t(name)}
                    subheader={
                      <>
                        <Typography
                          component="span"
                          //@ts-ignore
                          variant="subtitle"
                          className="sub-category-text"
                        >
                          {t(subCategory)}
                        </Typography>
                        {boothName && (
                          <Typography
                            component="span"
                            //@ts-ignore
                            variant="subtitle"
                            className="booth-name-text"
                          >
                            {`${t("Booth")} ${t(boothName)}`}
                          </Typography>
                        )}
                        <Typography
                          className="location-text"
                          component="span"
                          //@ts-ignore
                          variant="subtitle"
                        >
                          {_.compact([levelText, groupText]).join(" ")}
                        </Typography>
                      </>
                    }
                    image={featuredImage}
                    fallbackImageIcon={
                      <Box sx={comingSoonShopFallbackImageIconWrapper}>
                        {getIcon(
                          categoryIcon,
                          comingSoonShopFallbackImageIcon,
                          {
                            imageIconProps: {
                              solidColorFilterId: "comingShop_cover_filter",
                            },
                          }
                        )}
                      </Box>
                    }
                    avatar={
                      <Avatar src={logo} sx={comingSoonShopAvatar}>
                        {getIcon(categoryIcon, comingSoonShopAvatarFallBack, {
                          imageIconProps: {
                            solidColorFilterId: "comingShop_avatar_filter",
                          },
                        })}
                      </Avatar>
                    }
                    tag={t("Open Soon")}
                  />
                )
              })}
            </Box>
          ) : (
            <Box sx={emptyDataContainer}>
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </Box>
          )}
          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={filterByVenueId}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
        </Box>
      </Layout>
    </>
  )
}
